import {
  type JSX,
  useImperativeHandle,
  useLayoutEffect,
  useState,
} from "react";
import type { Ref } from "react";

import { componentManager, rwc } from "@frontend/bridge/ComponentManager";
import { Icon, type IconName } from "@frontend/components/atoms/Icon";
import { Toggle } from "@frontend/components/atoms/Toggle";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@frontend/components/atoms/Tooltip";

import { ToggleGroup } from "./ToggleGroup";
import {
  type ToggleGroupDataProps,
  toggleGroupDataSchema,
} from "./ToggleGroup.schema";
import { TOGGLE_GROUP_ITEM_TAG_NAME, TOGGLE_GROUP_TAG_NAME } from "./constants";

export interface ToggleGroupHandle {
  setValue: (value: string[]) => void;
}

declare module "@frontend/bridge/types" {
  interface ComponentRegistryMap {
    [TOGGLE_GROUP_TAG_NAME]: {
      props: ToggleGroupDataProps;
      handle: ToggleGroupHandle;
    };
  }
  interface InertTagNames {
    [TOGGLE_GROUP_ITEM_TAG_NAME]: true;
  }
}

const RWCToggleGroupItem = rwc(TOGGLE_GROUP_ITEM_TAG_NAME);

export function readToggleGroupSlotted(
  el: HTMLElement,
): Partial<ToggleGroupDataProps> {
  const toggles = Array.from(
    el.querySelectorAll<HTMLElement>(`:scope > ${RWCToggleGroupItem.tagName}`),
  ).map((item) => ({
    value: item.getAttribute("value") ?? "",
    label: item.textContent?.trim() ?? "",
    icon: (item.getAttribute("icon") ?? undefined) as IconName | undefined,
  }));

  return {
    toggles,
    onValueChange: (value) => {
      el.setAttribute("value", value.join(","));

      el.dispatchEvent(
        new CustomEvent("toggle-group:change", {
          detail: { value },
          bubbles: true,
        }),
      );
    },
  };
}

function RWCToggleGroupComponent({
  ref,
  toggles = [],
  defaultValue,
  value: controlledValue,
  onValueChange,
  size,
  iconOnly,
  ...props
}: Partial<ToggleGroupDataProps> & {
  ref?: Ref<ToggleGroupHandle>;
}): JSX.Element {
  const [value, setValue] = useState(controlledValue ?? defaultValue);

  useImperativeHandle(
    ref,
    () => ({
      setValue: (newValue) => {
        setValue(newValue);
        onValueChange?.(newValue);
      },
    }),

    [onValueChange],
  );

  useLayoutEffect(() => {
    if (defaultValue && controlledValue === undefined) {
      onValueChange?.(defaultValue);
    }
  }, []);

  useLayoutEffect(() => {
    if (
      controlledValue !== undefined &&
      controlledValue.join(",") !== value?.join(",")
    ) {
      setValue(controlledValue);
      onValueChange?.(controlledValue);
    }
  }, [controlledValue]);

  const handleValueChange = (newValue: string[]) => {
    setValue(newValue);
    onValueChange?.(newValue);
  };

  return (
    <ToggleGroup value={value} onValueChange={handleValueChange} {...props}>
      {toggles.map((toggleItem) =>
        iconOnly && toggleItem.icon ? (
          <Tooltip key={toggleItem.value}>
            <TooltipTrigger
              render={
                <Toggle
                  value={toggleItem.value}
                  size={size}
                  aria-label={toggleItem.label}
                >
                  <Icon name={toggleItem.icon as IconName} />
                </Toggle>
              }
            />
            <TooltipContent>{toggleItem.label}</TooltipContent>
          </Tooltip>
        ) : (
          <Toggle key={toggleItem.value} value={toggleItem.value} size={size}>
            {toggleItem.icon ? (
              <Icon name={toggleItem.icon as IconName} />
            ) : null}
            {toggleItem.label}
          </Toggle>
        ),
      )}
    </ToggleGroup>
  );
}

const RWCToggleGroup = rwc(TOGGLE_GROUP_TAG_NAME, {
  component: RWCToggleGroupComponent,
  dataSchema: toggleGroupDataSchema,
  usesSlots: true,
  readSlotted: readToggleGroupSlotted,
});

componentManager.register(RWCToggleGroupItem, RWCToggleGroup);
